<script setup lang="ts">
import type { IGQLBookingOverview } from "@/models/graphql/BookingOverviewResponse";
import { DateTime } from "luxon";
import { computed, defineProps, onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps<{
    bookingData: IGQLBookingOverview | null;
}>();

// A reactive value for the current time, updated every second
const now = ref(DateTime.now().setZone("Europe/London"));
let intervalId: number | undefined;

// Set up an interval to update 'now' when the component is mounted
onMounted(() => {
    intervalId = window.setInterval(() => {
        now.value = DateTime.now().setZone("Europe/London");
    }, 1000);
});

// Clear the interval when the component is unmounted to prevent memory leaks
onUnmounted(() => {
    clearInterval(intervalId);
});

// The expiry date is now taken directly from bookingExpiration
const expiryDate = computed(() => {
    if (!props.bookingData?.bookingExpiration) {
        // Return an invalid date if the source is not available
        return DateTime.invalid("No expiration date provided");
    }
    return DateTime.fromISO(props.bookingData.bookingExpiration);
});

// Watch for the expiration time to pass and then reload the page
watch(
    now,
    (currentNow) => {
        if (expiryDate.value.isValid && expiryDate.value < currentNow) {
            // Stop the timer before reloading to be safe
            if (intervalId) clearInterval(intervalId);
            window.location.reload();
        }
    },
    { immediate: true }
); // 'immediate' ensures the check runs on component mount

// Computes the human-readable relative time with the custom format
const relativeExpiryDate = computed(() => {
    if (!expiryDate.value.isValid) {
        return "..."; // Display placeholder for invalid or missing date
    }

    // Calculate the duration between the expiry date and now
    const duration = expiryDate.value.diff(now.value);

    // If time has run out, the watcher will trigger a reload soon
    if (duration.as("milliseconds") <= 0) {
        return "now";
    }

    // Convert duration into distinct units of hours, minutes, and seconds
    const { hours = 0, minutes = 0, seconds = 0 } = duration.shiftTo("hours", "minutes", "seconds").toObject();

    const intHours = Math.floor(hours);
    const intMinutes = Math.floor(minutes);
    const intSeconds = Math.floor(seconds);

    // Format output string based on the largest unit of time
    if (intHours >= 1) {
        const hourText = intHours === 1 ? "hour" : "hours";
        const minuteText = intMinutes === 1 ? "minute" : "minutes";
        return `in ${intHours} ${hourText} ${intMinutes} ${minuteText}`;
    }

    if (intMinutes >= 1) {
        const minuteText = intMinutes === 1 ? "minute" : "minutes";
        return `in ${intMinutes} ${minuteText}`;
    }

    const secondText = intSeconds === 1 ? "second" : "seconds";
    return `in ${intSeconds} ${secondText}`;
});
</script>

<template>
    <div class="basket-expiry">Expires {{ relativeExpiryDate }}</div>
</template>

<style scoped lang="scss">
@import "@/assets/base.scss";

.basket-expiry {
    height: 36px;
    grid-template-columns: min-content auto;
    line-height: normal;
    vertical-align: middle;
    align-items: center;
    display: inline;
    font-size: 0.7rem;
    color: #5c5c5c;
}
</style>
